import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle, Key, Phone, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [needsActivationCode, setNeedsActivationCode] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/contact`).then(res => setContactSettings(res.data)).catch(() => {});
  }, []);

  const processAuth = async (sessionId) => {
    try {
      const user = await loginWithGoogle(sessionId);
      toast.success(`Welcome, ${user?.name || 'User'}!`);
      // Navigate to dashboard with user data
      navigate('/dashboard', { state: { user }, replace: true });
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Google login failed. Please try again.';
      
      // Check if this is a new user who needs an activation code
      if (errorDetail === 'ACTIVATION_CODE_REQUIRED') {
        setNeedsActivationCode(true);
        setError('You need an activation code to register. Please go to the registration page and enter your code before signing up with Google.');
        toast.error('Activation code required for new accounts');
      } else if (errorDetail.includes('pending admin approval')) {
        setError(errorDetail);
        toast.error('Your account is pending approval');
      } else {
        setError(errorDetail);
        toast.error(errorDetail);
      }
    }
  };

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Extract session_id from URL hash (works on any route)
    const hash = window.location.hash || location.hash || '';
    const hashParams = new URLSearchParams(hash.replace('#', ''));
    let sessionId = hashParams.get('session_id');
    
    // Also check URL search params as fallback
    if (!sessionId) {
      const searchParams = new URLSearchParams(location.search);
      sessionId = searchParams.get('session_id');
    }


    if (!sessionId) {
      // no session_id
      setError('No session ID found. Please try logging in again.');
      return;
    }

    processAuth(sessionId);
  }, [loginWithGoogle, navigate, location]);

  const handleRetry = () => {
    setRetrying(true);
    // Redirect to Google login again
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  // Special UI for activation code required
  if (needsActivationCode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <div className="text-center text-white p-8 max-w-md">
          <div className="h-16 w-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
            <Key className="h-8 w-8 text-amber-500" />
          </div>
          <h2 className="text-xl font-bold mb-2">Activation Code Required</h2>
          <p className="text-white/70 mb-6">
            You need an activation code to create a new account. Please contact us to purchase one:
          </p>
          
          {/* Contact buttons */}
          <div className="bg-gradient-to-r from-teal-500/20 to-emerald-500/20 border border-teal-500/30 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-3 mb-3">
              <a 
                href={contactSettings ? `https://wa.me/${contactSettings.whatsapp_number}?text=${encodeURIComponent(contactSettings.whatsapp_message || '')}` : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
              </a>
              <a 
                href={contactSettings ? `tel:${contactSettings.phone_number}` : '#'}
                className="flex-1"
              >
                <Button 
                  variant="outline" 
                  className="w-full border-white/30 text-white hover:bg-white/10"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call Us
                </Button>
              </a>
            </div>
            <p className="text-sm text-teal-300">{contactSettings?.phone_display || ''}</p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Link to="/register">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                <Key className="h-4 w-4 mr-2" />
                Go to Registration
              </Button>
            </Link>
            <Button 
              onClick={handleGoToLogin}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center text-white p-8 max-w-md">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Google Login Failed</h2>
          <p className="text-white/70 mb-6">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button 
              onClick={handleRetry} 
              disabled={retrying}
              className="bg-teal-600 hover:bg-teal-700"
            >
              {retrying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Try Again
            </Button>
            <Button 
              onClick={handleGoToLogin}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="text-center text-white">
        <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-teal-500" />
        <p className="text-lg">Signing you in with Google...</p>
        <p className="text-sm text-white/60 mt-2">Please wait...</p>
      </div>
    </div>
  );
}
